/**
 * DesktopSidebar - Right Panel for Desktop Layout
 * 
 * Contains:
 * - Compact FloatingActionBar
 * - Quick contact info
 * - Primary CTA
 */

import React from 'react';
import { Phone, Navigation, Star, MapPin, BookOpen, ChevronRight } from 'lucide-react';

interface DesktopSidebarProps {
  hasPhone: boolean;
  hasMenu: boolean;
  isOpen: boolean;
  openStatusText: string;
  address: string;
  onCall: () => void;
  onRoute: () => void;
  onReview: () => void;
  onViewMenu: () => void;
}

export const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  hasPhone,
  hasMenu,
  isOpen,
  openStatusText,
  address,
  onCall,
  onRoute,
  onReview,
  onViewMenu,
}) => {
  return (
    <div className="space-y-5">
      {/* Quick Actions Card — Premium glass */}
      <div 
        className="p-6 rounded-[20px] overflow-hidden"
        style={{
          background: 'linear-gradient(to bottom, rgba(255,255,255,0.98), rgba(255,255,255,0.92))',
          backdropFilter: 'blur(16px)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.04)',
        }}
      >
        {/* Status — Premium pill */}
        <div className="flex items-center gap-2.5 mb-6">
          <div 
            className="flex items-center gap-2 px-3.5 py-2 rounded-full"
            style={{
              background: isOpen ? 'rgba(14,127,79,0.08)' : 'rgba(239,68,68,0.08)',
            }}
          >
            <div 
              className="w-2 h-2 rounded-full"
              style={{
                background: isOpen ? '#0E7F4F' : '#EF4444',
                boxShadow: isOpen 
                  ? '0 0 6px rgba(14,127,79,0.4)' 
                  : '0 0 6px rgba(239,68,68,0.4)',
              }}
            />
            <span className={`text-sm font-semibold ${isOpen ? 'text-emerald-700' : 'text-red-600'}`}>
              {openStatusText}
            </span>
          </div>
        </div>
        
        {/* Action Buttons — refined grid */}
        <div className="grid grid-cols-3 gap-2 mb-6">
          <button
            onClick={onCall}
            disabled={!hasPhone}
            className={`flex flex-col items-center gap-2.5 py-3.5 rounded-2xl transition-colors ${
              hasPhone ? 'hover:bg-emerald-50/80 active:scale-95' : 'opacity-35 cursor-not-allowed'
            }`}
          >
            <div 
              className="w-12 h-12 rounded-[14px] flex items-center justify-center"
              style={{ 
                background: hasPhone 
                  ? 'linear-gradient(135deg, rgba(14,127,79,0.12), rgba(14,127,79,0.06))'
                  : 'rgba(156,163,175,0.1)',
              }}
            >
              <Phone className="h-5 w-5" style={{ color: hasPhone ? '#0E7F4F' : '#9CA3AF' }} strokeWidth={1.8} />
            </div>
            <span className="text-xs font-semibold text-gray-700">Anrufen</span>
          </button>
          
          <button
            onClick={onRoute}
            className="flex flex-col items-center gap-2.5 py-3.5 rounded-2xl transition-colors hover:bg-blue-50/80 active:scale-95"
          >
            <div 
              className="w-12 h-12 rounded-[14px] flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.12), rgba(37,99,235,0.06))' }}
            >
              <Navigation className="h-5 w-5 text-blue-600" strokeWidth={1.8} />
            </div>
            <span className="text-xs font-semibold text-gray-700">Route</span>
          </button>
          
          <button
            onClick={onReview}
            className="flex flex-col items-center gap-2.5 py-3.5 rounded-2xl transition-colors hover:bg-amber-50/80 active:scale-95"
          >
            <div 
              className="w-12 h-12 rounded-[14px] flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(245,158,11,0.06))' }}
            >
              <Star className="h-5 w-5 text-amber-500" strokeWidth={1.8} />
            </div>
            <span className="text-xs font-semibold text-gray-700">Bewerten</span>
          </button>
        </div>
        
        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-5" />
        
        {/* Address */}
        <button
          onClick={onRoute}
          className="flex items-center gap-3 w-full py-2.5 px-1 rounded-xl transition-colors hover:bg-gray-50/80 active:scale-[0.99]"
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(107,114,128,0.08)' }}>
            <MapPin className="h-4 w-4 text-gray-500" strokeWidth={1.8} />
          </div>
          <span className="text-sm text-gray-600 text-left truncate">{address}</span>
        </button>
      </div>
      
      {/* Menu CTA — Premium card with preview hint */}
      {hasMenu && (
        <button
          onClick={onViewMenu}
          className="w-full p-5 rounded-[20px] text-left transition-all hover:scale-[1.01] active:scale-[0.99] group"
          style={{
            background: 'linear-gradient(160deg, #f7f9f6 0%, #eef3ea 50%, #e6eee0 100%)',
            border: '1px solid rgba(14,127,79,0.08)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.03), 0 4px 20px rgba(14,127,79,0.04)',
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ 
                background: 'linear-gradient(135deg, rgba(14,127,79,0.12), rgba(14,127,79,0.05))',
              }}
            >
              <BookOpen className="h-5 w-5 text-emerald-700" strokeWidth={1.8} />
            </div>
            <div>
              <span className="text-[13px] font-bold text-gray-900 block">Speisekarte</span>
              <span className="text-[11px] text-gray-500">Jetzt entdecken</span>
            </div>
          </div>
          <div 
            className="w-full py-3 rounded-xl flex items-center justify-center gap-2 transition-all group-hover:gap-3"
            style={{
              background: 'linear-gradient(170deg, #1ba85e 0%, #0E7F4F 40%, #0a6b3f 100%)',
              boxShadow: '0 4px 16px rgba(14,127,79,0.3), 0 1px 0 rgba(255,255,255,0.12) inset',
            }}
          >
            <BookOpen className="h-4 w-4 text-white" strokeWidth={2.2} />
            <span className="text-white font-bold text-[13px]">Speisekarte ansehen</span>
            <ChevronRight className="h-4 w-4 text-white/70 transition-transform group-hover:translate-x-0.5" strokeWidth={2.5} />
          </div>
        </button>
      )}
      
      {/* Primary Call CTA */}
      {hasPhone && (
        <button
          onClick={onCall}
          className="w-full py-4 px-6 rounded-2xl flex items-center justify-center gap-2.5 transition-all hover:opacity-95 active:scale-[0.98]"
          style={{
            background: hasMenu 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #0E7F4F 0%, #0a6b42 50%, #18B66A 100%)',
            boxShadow: hasMenu
              ? '0 4px 16px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.1)'
              : '0 4px 20px rgba(14,127,79,0.3), 0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <Phone className="h-5 w-5 text-white" strokeWidth={2} />
          <span className="text-white font-semibold text-[15px]">Jetzt anrufen</span>
        </button>
      )}
    </div>
  );
};

export default DesktopSidebar;
